<template>
  <transition name="bubble-fade">
    <div 
      v-if="showBubble && currentPayment"
      @mouseenter="pauseRotation"
      @mouseleave="resumeRotation"
      class="payment-bubble"
    >
      <div class="bubble-content">
        <div class="avatar-section">
          <div class="avatar" :style="{ backgroundColor: currentPayment.color }">
            <v-icon color="white">{{ currentPayment.icon }}</v-icon>
          </div>
        </div>
        
        <div class="info-section">
          <div class="user-email">{{ currentPayment.email }}</div>
          <div class="payment-details">
            <span class="action-text">{{ currentPayment.actionText }}</span>
            <span class="amount-highlight">${{ currentPayment.amount }} USD</span>
          </div>
          <div class="platform-info" v-if="currentPayment.platform">
            <v-icon size="12" color="#9C27B0">mdi-web</v-icon>
            <span>{{ currentPayment.platform }}</span>
          </div>
          <div class="timestamp">
            <v-icon size="12" color="#999">mdi-clock-outline</v-icon>
            <span>{{ currentPayment.time }}</span>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from '@/axios.js';

export default {
  name: 'PaymentNotificationBubble',
  data() {
    return {
      showBubble: false,
      currentIndex: 0,
      rotationInterval: null,
      isPaused: false,
      payments: [],
      usedIndices: [] // Para evitar repeticiones inmediatas
    };
  },
  computed: {
    currentPayment() {
      if (this.payments.length === 0) return null;
      return this.payments[this.currentIndex];
    }
  },
  mounted() {
    this.getWithdrawals();
  },
  beforeDestroy() {
    this.stopRotation();
  },
  methods: {
   startRotation() {
      if (this.payments.length === 0) return;
      
      // Delay inicial aleatorio entre 1-2 segundos
      setTimeout(() => {
        this.showBubble = true;
        this.scheduleNextRotation();
      }, this.getRandomDelay(1000, 2000));
    },
    
    scheduleNextRotation() {
      // Tiempo aleatorio entre rotaciones: 4-8 segundos
      const rotationTime = this.getRandomDelay(4000, 8000);
      
      this.rotationInterval = setTimeout(() => {
        if (!this.isPaused) {
          this.showBubble = false;
          
          // Tiempo de fade aleatorio: 800-1500ms
          const fadeTime = this.getRandomDelay(800, 1500);
          
          setTimeout(() => {
            this.currentIndex = this.getRandomIndex();
            this.showBubble = true;
            this.scheduleNextRotation(); // Recursivo con nuevo tiempo aleatorio
          }, fadeTime);
        } else {
          this.scheduleNextRotation(); // Reintentar si está pausado
        }
      }, rotationTime);
    },
    
    stopRotation() {
      if (this.rotationInterval) {
        clearTimeout(this.rotationInterval);
      }
    },
    
    getRandomIndex() {
      if (this.payments.length === 1) return 0;
      
      // Si hemos usado más del 70% de los índices, resetear
      if (this.usedIndices.length > this.payments.length * 0.7) {
        this.usedIndices = [];
      }
      
      let newIndex;
      let attempts = 0;
      
      do {
        newIndex = Math.floor(Math.random() * this.payments.length);
        attempts++;
        
        // Después de 10 intentos, aceptar cualquier índice diferente al actual
        if (attempts > 10) {
          if (newIndex !== this.currentIndex) break;
        }
      } while (
        this.usedIndices.includes(newIndex) || 
        newIndex === this.currentIndex
      );
      
      this.usedIndices.push(newIndex);
      return newIndex;
    },
    
    getRandomDelay(min, max) {
      return Math.floor(Math.random() * (max - min + 1)) + min;
    },
    pauseRotation() {
      this.isPaused = true;
    },
    resumeRotation() {
      this.isPaused = false;
      // Reanudar con nuevo tiempo aleatorio
      if (!this.rotationInterval) {
        this.scheduleNextRotation();
      }
    },
    getWithdrawals() {
      axios.get('get-withdrawal-users')
        .then(response => {
          // Mezclar aleatoriamente el array antes de procesarlo
          const shuffled = this.shuffleArray(response.data.withdrawals);
          
          this.payments = shuffled.map(item => {
            const isConversion = item.type === 'conversiones';
            const isAds = isConversion && item.platform === 'Ads';
            
            let actionText = '';
            if (isConversion) {
              actionText = isAds 
                ? this.$t('moreMoney.paymentBubble.convertedAds')
                : this.$t('moreMoney.paymentBubble.converted');
            } else {
              actionText = this.$t('moreMoney.paymentBubble.withdrew');
            }

            return {
              email: item.email,
              amount: item.amount,
              type: item.type,
              platform: isConversion ? item.platform : null,
              actionText: actionText,
              icon: isConversion ? 'mdi-sync' : 'mdi-cash-multiple',
              time: this.generateRandomTime(),
              color: this.generateRandomColor()
            };
          });
          
          // Iniciar rotación después de cargar datos
          this.$nextTick(() => {
            this.startRotation();
          });
        })
        .catch(error => {
          console.error('Error al obtener los retiros:', error);
        });
    },
    
    shuffleArray(array) {
      const shuffled = [...array];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    },
    
    generateRandomTime() {
      const options = [
        { max: 59, unit: this.$t('moreMoney.paymentBubble.timeUnits.minutes'), weight: 60 },
        { max: 23, unit: this.$t('moreMoney.paymentBubble.timeUnits.hours'), weight: 40 }
      ];
      
      // Selección ponderada (más probabilidad para minutos)
      const totalWeight = options.reduce((sum, opt) => sum + opt.weight, 0);
      let random = Math.random() * totalWeight;
      
      let selectedOption = options[0];
      for (const option of options) {
        if (random < option.weight) {
          selectedOption = option;
          break;
        }
        random -= option.weight;
      }
      
      const value = Math.floor(Math.random() * selectedOption.max) + 1;
      return `${this.$t('moreMoney.paymentBubble.ago')} ${value} ${selectedOption.unit}`;
    },
    generateRandomColor() {
      const colors = [
        '#00D4AA', // Verde turquesa
        '#9C27B0', // Morado
        '#FF6B35', // Naranja
        '#2196F3', // Azul
        '#E91E63', // Rosa
        '#FF9800', // Naranja dorado
        '#00BCD4', // Cian
        '#4CAF50', // Verde
        '#673AB7', // Morado oscuro
        '#F44336'  // Rojo
      ];
      
      return colors[Math.floor(Math.random() * colors.length)];
    }
  }
};
</script>

<style scoped>
.payment-bubble {
  position: fixed;
  z-index: 2;
  margin-top: 30px;
  left: 20px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%);
  border-radius: 40px;
  padding: 12px 18px;
  min-width: 300px;
  max-width: 310px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid rgba(156, 39, 176, 0.15);
  backdrop-filter: blur(12px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}


.bubble-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.avatar-section {
  flex-shrink: 0;
}

.avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  animation: pulse 2s ease-in-out infinite;
}

.avatar-icon {
  font-size: 24px;
  font-weight: bold;
  color: white;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.info-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.user-email {
  font-weight: 700;
  font-size: 14px;
  color: #9C27B0;
  letter-spacing: 0.2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.payment-details {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.action-text {
  color: #666;
  font-weight: 500;
  font-size: 13px;
}

.amount-highlight {
  font-weight: 800;
  font-size: 16px;
  color: #00D4AA;
  text-shadow: 0 2px 2px rgba(0, 212, 170, 0.25);
  letter-spacing: 0.3px;
}

.timestamp {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #999;
  font-style: italic;
}

.platform-info {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #9C27B0;
  font-weight: 600;
}

/* Animaciones de transición */
.bubble-fade-enter-active {
  animation: slideInLeft 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.bubble-fade-leave-active {
  animation: slideOutLeft 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-100px) scale(0.8);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}

@keyframes slideOutLeft {
  from {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateX(-100px) scale(0.8);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .payment-bubble {
    margin: 16px auto;
    min-width: 260px;
    max-width: 280px;
    padding: 10px 14px;
    border-radius: 24px;
  }
  
  .bubble-content {
    gap: 10px;
  }
  
  .avatar {
    width: 40px;
    height: 40px;
  }
  
  .avatar-icon {
    font-size: 18px;
  }
  
  .user-email {
    font-size: 12px;
  }
  
  .action-text {
    font-size: 11px;
  }
  
  .amount-highlight {
    font-size: 14px;
  }
  
  .platform-info {
    font-size: 11px;
  }
  
  .timestamp {
    font-size: 10px;
  }
}

@media (max-width: 480px) {
  .payment-bubble {
    margin: 12px auto;
    min-width: 240px;
    max-width: 260px;
    padding: 8px 12px;
    border-radius: 20px;
  }
  
  .bubble-content {
    gap: 12px;
  }
}
</style>
